import { useState, useRef, useEffect, useCallback } from 'react';
import {
  TranscribeStreamingClient,
  StartStreamTranscriptionCommand,
} from '@aws-sdk/client-transcribe-streaming';
import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
} from '@aws-sdk/client-cognito-identity';
import { fetchAuthSession } from 'aws-amplify/auth';

interface TranscriptResult {
  speaker_label?: string;
  content: string;
  timestamp?: string;
}

interface UseTranscribeMicProps {
  selectedLanguage?: string;
  selectedMic?: string;
}

const SUPPORTED_LANGUAGE_CODES = ['en-US', 'ar-SA'];
const DEFAULT_SAMPLE_RATE = 16000;

const useTranscribeMic = ({ selectedLanguage = 'Auto-detect', selectedMic }: UseTranscribeMicProps = {}) => {
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [transcribedText, setTranscribedText] = useState<TranscriptResult[]>([]);

  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorNodeRef = useRef<ScriptProcessorNode | null>(null);
  const audioQueueRef = useRef<Uint8Array[]>([]);
  const isProcessingRef = useRef(false);
  const isMutedRef = useRef(false);
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const maxReconnectAttempts = 3;

  const initTranscribeClient = async (): Promise<TranscribeStreamingClient> => {
    try {
      const { tokens } = await fetchAuthSession();
      if (!tokens?.idToken?.toString()) {
        throw new Error('No valid ID token found.');
      }
      const regionFromEnv = import.meta.env.VITE_AWS_REGION;
      const userPoolIdFromEnv = import.meta.env.VITE_COGNITO_USER_POOL_ID;
      const identityPoolIdFromEnv = import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID;
      if (!regionFromEnv || !userPoolIdFromEnv || !identityPoolIdFromEnv) {
        throw new Error("AWS environment variables are not configured.");
      }
      const cognitoIdentityClient = new CognitoIdentityClient({ region: regionFromEnv });
      const issuerUrl = `cognito-idp.${regionFromEnv}.amazonaws.com/${userPoolIdFromEnv}`;
      const { IdentityId } = await cognitoIdentityClient.send(
        new GetIdCommand({ IdentityPoolId: identityPoolIdFromEnv, Logins: { [issuerUrl]: tokens.idToken.toString() } })
      );
      if (!IdentityId) throw new Error('Could not get Identity ID.');
      const { Credentials } = await cognitoIdentityClient.send(
        new GetCredentialsForIdentityCommand({ IdentityId, Logins: { [issuerUrl]: tokens.idToken.toString() } })
      );
      if (!Credentials?.AccessKeyId || !Credentials?.SecretKey || !Credentials?.SessionToken) {
        throw new Error('Could not retrieve temporary credentials.');
      }
      return new TranscribeStreamingClient({
        region: regionFromEnv,
        credentials: {
          accessKeyId: Credentials.AccessKeyId,
          secretAccessKey: Credentials.SecretKey,
          sessionToken: Credentials.SessionToken,
          expiration: Credentials.Expiration,
        },
      });
    } catch (error) {
      console.error('Error initializing Transcribe client:', error);
      throw error;
    }
  };
  
  const stopTranscription = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (processorNodeRef.current && audioContextRef.current) {
      processorNodeRef.current.disconnect();
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(e => console.error("Error closing AudioContext:", e));
      }
      processorNodeRef.current = null;
      audioContextRef.current = null;
    }
    
    if(isProcessingRef.current) {
        isProcessingRef.current = false;
        audioQueueRef.current = [];
        setIsTranscribing(false);
        setIsMuted(false);
        isMutedRef.current = false;
        console.log('🔴 Transcription stopped and resources cleaned up.');
    }
  }, []);

  const processTranscriptionResult = useCallback((result: any) => {
    if (result.IsPartial) {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      silenceTimerRef.current = setTimeout(() => {
        console.log('Silence detected for 5 seconds, stopping transcription.');
        stopTranscription();
      }, 5000);
      
      const transcript = result.Alternatives?.[0]?.Transcript || '';
      setCurrentTranscript(transcript);
    } else {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      const transcript = result.Alternatives?.[0]?.Transcript || '';
      if (transcript.trim()) {
        const newMessage: TranscriptResult = {
          content: transcript,
          timestamp: new Date().toISOString(),
        };
        setTranscribedText((prev) => [...prev, newMessage]);
        setCurrentTranscript('');
      }
    }
  }, [stopTranscription]);

  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    isMutedRef.current = newMutedState;
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(track => { track.enabled = !newMutedState; });
    }
    console.log(newMutedState ? '🔇 Microphone muted' : '🔊 Microphone unmuted');
  };

  const startTranscription = useCallback(async () => {
    if (isTranscribing) return;
    
    // FIX: Don't clear transcribedText on restart - append to existing content
    // setTranscribedText([]); // Remove this line
    setCurrentTranscript('');
    isProcessingRef.current = true;

    try {
      console.log('🎤 Starting transcription...');
      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: DEFAULT_SAMPLE_RATE,
      };

      // Use selected microphone if provided
      if (selectedMic) {
        audioConstraints.deviceId = { exact: selectedMic };
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      streamRef.current = stream;

      const audioContext = new AudioContext({ sampleRate: DEFAULT_SAMPLE_RATE });
      audioContextRef.current = audioContext;

      const sourceNode = audioContext.createMediaStreamSource(stream);
      const processorNode = audioContext.createScriptProcessor(4096, 1, 1);
      processorNodeRef.current = processorNode;

      processorNode.onaudioprocess = (event) => {
        if (!isProcessingRef.current) return;
        const inputData = event.inputBuffer.getChannelData(0);
        const pcmData = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          pcmData[i] = Math.max(-1, Math.min(1, inputData[i])) * 0x7FFF;
        }
        audioQueueRef.current.push(new Uint8Array(pcmData.buffer));
      };
      sourceNode.connect(processorNode);
      processorNode.connect(audioContext.destination);

      const transcribeClient = await initTranscribeClient();

      const audioStream = async function*() {
        while (isProcessingRef.current) {
            if (audioQueueRef.current.length > 0) {
                const chunk = audioQueueRef.current.shift();
                if(chunk) {
                    yield { AudioEvent: { AudioChunk: chunk } };
                }
            } else {
                await new Promise(resolve => setTimeout(resolve, 50)); 
            }
        }
      };
      
      setIsTranscribing(true);

      let commandParams: any = {
        MediaEncoding: 'pcm',
        MediaSampleRateHertz: DEFAULT_SAMPLE_RATE,
        AudioStream: audioStream(),
      };

      if (selectedLanguage === 'Auto-detect') {
        commandParams = {
          ...commandParams,
          IdentifyLanguage: true,
          LanguageOptions: SUPPORTED_LANGUAGE_CODES.join(','), 
          PreferredLanguages: SUPPORTED_LANGUAGE_CODES,
        };
      } else {
        const specificLanguageCode = selectedLanguage === 'English' ? 'en-US' : (selectedLanguage === 'Arabic' ? 'ar-SA' : 'en-US'); 
        commandParams = {
          ...commandParams,
          LanguageCode: specificLanguageCode,
        };
      }

      const command = new StartStreamTranscriptionCommand(commandParams);
      const data = await transcribeClient.send(command);

      if (data.TranscriptResultStream) {
        // Reset reconnect attempts on successful connection
        reconnectAttemptsRef.current = 0;

        for await (const event of data.TranscriptResultStream) {
            if (!isProcessingRef.current) break;
            if (event.TranscriptEvent?.Transcript?.Results) {
                event.TranscriptEvent.Transcript.Results.forEach(processTranscriptionResult);
            }
        }
      }
    } catch (error: any) {
      console.error('❌ Error in transcription:', error);

      // Check if this is a recoverable network error
      const isNetworkError = error.name === 'NetworkError' ||
        error.message?.includes('network') ||
        error.message?.includes('connection') ||
        error.code === 'ECONNRESET';

      if (isNetworkError && reconnectAttemptsRef.current < maxReconnectAttempts && isProcessingRef.current) {
        reconnectAttemptsRef.current++;
        console.log(`[Transcribe] Network error, attempting reconnect (${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`);

        // Wait before reconnecting (exponential backoff)
        const backoffMs = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 5000);
        await new Promise(resolve => setTimeout(resolve, backoffMs));

        // Try to restart transcription
        try {
          console.log('[Transcribe] Reconnecting transcription stream...');
          return startTranscription();
        } catch (reconnectError) {
          console.error('[Transcribe] Reconnection failed:', reconnectError);
        }
      }

      stopTranscription();
      throw error;
    }
  }, [isTranscribing, processTranscriptionResult, stopTranscription, selectedLanguage, selectedMic]);

  // FIX: Add a method to clear transcript when needed
  const clearTranscribedText = useCallback(() => {
    setTranscribedText([]);
    setCurrentTranscript('');
  }, []);

  // Handle visibility change - resume AudioContext when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isProcessingRef.current) {
        // Resume AudioContext if it was suspended (browsers suspend on tab switch)
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          try {
            await audioContextRef.current.resume();
            console.log('[Transcribe] AudioContext resumed after visibility change');
          } catch (err) {
            console.error('[Transcribe] Failed to resume AudioContext:', err);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    return () => {
      console.log("Component unmounting, initiating cleanup.");
      stopTranscription();
    };
  }, [stopTranscription]);

  return {
    isTranscribing,
    isMuted,
    currentTranscript,
    transcribedText,
    startTranscription,
    stopTranscription,
    toggleMute,
    clearTranscribedText
  };
};

export default useTranscribeMic;